import React, { useMemo, useRef, useState } from 'react';
import { FOCUS, MOTION, SkeletonRows } from '../styles';
import { Inventory, InventoryTopic, SERIES_DRAG_TYPE, SeriesRef } from './layoutTypes';
import SearchField from '../../components/ui/SearchField';
import Tooltip from '../../components/ui/Tooltip';
import Alert from '../../components/ui/Alert';
import EmptyState from '../../components/ui/EmptyState';
import Reveal from '../../components/ui/Reveal';
import FadeIn from '../../components/ui/FadeIn';

export interface SeriesTreeProps {
  inventory: Inventory | null;
  loading: boolean;
  error: string | null;
  onAdd: (ref: SeriesRef) => void;
  /** Where a click lands: the selected plot's title, or null for a new plot. */
  focusedPlotTitle: string | null;
  onRetry?: () => void;
  id?: string;
}

const topicKey = (t: InventoryTopic) => `${t.name}|${t.instance}`;

const fmtRows = (n: number) => {
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M rows`;
  if (n >= 1e4) return `${Math.round(n / 1e3)}k rows`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}k rows`;
  return `${n} ${n === 1 ? 'row' : 'rows'}`;
};

const fmtRate = (hz: number) => (hz >= 10 ? `${Math.round(hz)} Hz` : `${hz.toFixed(1)} Hz`);

const Chevron: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    className={`h-3.5 w-3.5 shrink-0 transition-transform duration-150 ease-emphasized motion-reduce:transition-none ${open ? 'rotate-90' : ''}`}
  >
    <path fill="currentColor" d="M6 3.5 10.5 8 6 12.5 5 11.4 8.4 8 5 4.6z" />
  </svg>
);

const ITEM = `np-state flex w-full min-w-0 items-center gap-1.5 rounded px-2 text-left ${FOCUS} ${MOTION}`;

/** Arrow keys walk the visible rows; Tab still leaves the list. */
const walk = (e: React.KeyboardEvent<HTMLElement>, list: HTMLElement | null) => {
  if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
  const items = Array.from(list?.querySelectorAll<HTMLElement>('[data-tree-item]') || []);
  if (items.length === 0) return;
  e.preventDefault();
  const at = items.indexOf(document.activeElement as HTMLElement);
  const next = e.key === 'ArrowDown' ? Math.min(items.length - 1, at + 1) : Math.max(0, at - 1);
  items[at < 0 ? 0 : next].focus();
};

const SeriesTree: React.FC<SeriesTreeProps> = ({
  inventory, loading, error, onAdd, focusedPlotTitle, onRetry, id,
}) => {
  const [query, setQuery] = useState('');
  // With a query, topics opened by a field match start open; a toggle flips whichever state it is in.
  const [toggled, setToggled] = useState<Set<string>>(new Set());
  const listRef = useRef<HTMLDivElement>(null);

  const multiInstance = useMemo(() => {
    const counts = new Map<string, number>();
    (inventory?.topics || []).forEach(t => counts.set(t.name, (counts.get(t.name) || 0) + 1));
    return counts;
  }, [inventory]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (inventory?.topics || []).flatMap(t => {
      if (!q) return [{ topic: t, fields: t.fields, autoOpen: false }];
      if (t.name.toLowerCase().includes(q)) return [{ topic: t, fields: t.fields, autoOpen: false }];
      const fields = t.fields.filter(f => f.name.toLowerCase().includes(q));
      return fields.length ? [{ topic: t, fields, autoOpen: true }] : [];
    });
  }, [inventory, query]);

  const setSearch = (v: string) => {
    setQuery(v);
    setToggled(new Set());
  };

  const toggle = (k: string) => setToggled(prev => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });

  const startDrag = (e: React.DragEvent, ref: SeriesRef) => {
    e.dataTransfer.setData(SERIES_DRAG_TYPE, JSON.stringify(ref));
    e.dataTransfer.setData('text/plain', `${ref.topic}.${ref.field}`);
    e.dataTransfer.effectAllowed = 'copy';
  };

  return (
    <aside
      id={id}
      aria-label="Fields in this log"
      className="flex h-full min-h-0 w-64 shrink-0 flex-col overflow-hidden rounded-lg bg-surface-container-low"
    >
      <div className="flex flex-col gap-1.5 bg-surface-container px-2 py-2">
        <SearchField
          size="sm"
          value={query}
          onChange={setSearch}
          onKeyDown={e => walk(e, listRef.current)}
          placeholder="Search topics and fields"
          label="Search topics and fields"
        />
        <p className="truncate px-1 text-xs text-on-surface-variant" aria-live="polite">
          {focusedPlotTitle ? `Click adds to ${focusedPlotTitle}. Drag to any plot.` : 'Click adds a new plot. Drag to any plot.'}
        </p>
      </div>

      <FadeIn key={inventory ? 'tree' : error ? 'error' : 'loading'} className="flex min-h-0 flex-1 flex-col">
      {loading && !inventory && <SkeletonRows rows={8} />}

      {error && !inventory && (
        <div className="p-3">
          <Alert variant="note" onRetry={onRetry} retryLabel="Retry" retryTip="Load the field list again">{error}</Alert>
        </div>
      )}

      {inventory && (
        <div
          ref={listRef}
          className="dt-scroll min-h-0 flex-1 overflow-y-auto px-1 py-1"
          onKeyDown={e => walk(e, listRef.current)}
        >
          <Reveal show={rows.length === 0}>
            <EmptyState variant="area">
              {query ? `Nothing matches "${query.trim()}".` : 'This log has no topics.'}
            </EmptyState>
          </Reveal>
          <ul className="flex flex-col gap-px">
            {rows.map(({ topic, fields, autoOpen }) => {
              const k = topicKey(topic);
              const open = autoOpen !== toggled.has(k);
              const showInstance = (multiInstance.get(topic.name) || 0) > 1;
              const listId = `fields-${k.replace(/[^A-Za-z0-9_-]/g, '-')}`;
              return (
                <li key={k}>
                  <Tooltip label={`${topic.name}${showInstance ? ` instance ${topic.instance}` : ''}, ${topic.rows} rows`}>
                  <button
                    type="button"
                    data-tree-item
                    aria-expanded={open}
                    aria-controls={open ? listId : undefined}
                    onClick={() => toggle(k)}
                    className={`${ITEM} py-1.5 text-sm text-on-surface`}
                  >
                    <span className="text-on-surface-variant"><Chevron open={open} /></span>
                    <span className="min-w-0 truncate font-mono text-xs">
                      {topic.name}
                      {showInstance && <span className="text-primary">[{topic.instance}]</span>}
                    </span>
                    <span className="ml-auto shrink-0 whitespace-nowrap text-[11px] tabular-nums text-on-surface-variant">
                      {fmtRows(topic.rows)}
                      {topic.rate_hz > 0 && `, ${fmtRate(topic.rate_hz)}`}
                    </span>
                  </button>
                  </Tooltip>
                  <Reveal show={open}>
                    <ul id={listId} className="mb-1 ml-4 flex flex-col gap-px">
                      {fields.map(f => {
                        const ref: SeriesRef = { topic: topic.name, instance: topic.instance, field: f.name };
                        return (
                          <li key={f.name}>
                            <Tooltip label={`${f.name}: click to plot, or drag to a plot`}>
                            <button
                              type="button"
                              data-tree-item
                              draggable
                              onDragStart={e => startDrag(e, ref)}
                              onClick={() => onAdd(ref)}
                              aria-label={`Plot ${topic.name}${showInstance ? `[${topic.instance}]` : ''}.${f.name}`}
                              className={`${ITEM} cursor-grab py-1 text-xs text-on-surface-variant hover:text-on-surface active:cursor-grabbing`}
                            >
                              <span className="min-w-0 truncate font-mono">{f.name}</span>
                              {f.kind !== 'float' && (
                                <span className="ml-auto shrink-0 text-[11px] text-on-surface-variant">{f.kind}</span>
                              )}
                            </button>
                            </Tooltip>
                          </li>
                        );
                      })}
                    </ul>
                  </Reveal>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      </FadeIn>
    </aside>
  );
};

export default React.memo(SeriesTree);
